import 'gleam-shared/embeds/CapturePreview.scss'

import Emitter from 'gleam-shared/helpers/emitter'
import debounce from 'gleam-shared/helpers/debounce'
import extractKey from 'gleam-shared/domain/extractKey'
import onceDetaches from 'gleam-shared/browser/onceDetaches'
import extractOrigin from 'gleam-shared/network/extractOrigin'
import { writeDom, cancelFastDom } from 'gleam-shared/browser/fastDom'

import DeadPreview from 'gleam-shared/embeds/DeadPreview'
import BasePreview from 'gleam-shared/embeds/BasePreview'

const BACKGROUND_TILES = [
  'capture-preview__skeleton capture-preview__skeleton--capture',
  'capture-preview__skeleton capture-preview__skeleton--square',
  'capture-preview__skeleton capture-preview__skeleton--article-1',
  'capture-preview__skeleton capture-preview__skeleton--article-2',
  'capture-preview__skeleton capture-preview__skeleton--article-3',
  'capture-preview__skeleton capture-preview__skeleton--row-1',
  'capture-preview__skeleton capture-preview__skeleton--long-1',
  'capture-preview__skeleton capture-preview__skeleton--short-1',
  'capture-preview__skeleton capture-preview__skeleton--row-2',
  'capture-preview__skeleton capture-preview__skeleton--short-2',
  'capture-preview__skeleton capture-preview__skeleton--long-2',
  'capture-preview__skeleton capture-preview__skeleton--row-3'
]

const DEBOUNCE = 300

export function injectPlaceholder(parent) {
  preconfigure(parent)
}

export default function CapturePreview(parent, url) {
  try {
    const frameSrc = buildFrameSrc(url)

    return RealPreview(parent, frameSrc)
  } catch (err) {
    return DeadPreview('The capture preview', err)
  }
}

function RealPreview(parent, frameSrc) {
  const preview = BasePreview({
    destroy,
    description: `The ${extractKey(frameSrc)} capture`,
    hideEmitter: Emitter()
  })

  let frameShown = false
  let frameReady = false
  let frameHeight = null

  const frame = buildFrame()
  const debouncedShow = debounce(lazyShow, DEBOUNCE)
  const detachObserver = onceDetaches(parent, destroy)

  window.addEventListener('message', onMessage)
  writeDom(configure)

  return preview

  function configure() {
    preconfigure(parent)
    parent.append(frame)
    setFrameSrc(frame, frameSrc)
  }

  function destroy() {
    debouncedShow.cancel()
    detachObserver.destroy()
    cancelFastDom(configure)
    cancelFastDom(show)
    cancelFastDom(applyHeight)
    window.removeEventListener('message', onMessage)
  }

  function onMessage({ source, data }) {
    if (source !== frame.contentWindow) {
      return
    }

    const payload = parsePayload(data)

    if (/capture_preview:hide/.test(payload.command)) {
      hide()
    } else if (/capture_preview:ready/.test(payload.command)) {
      ready()
    } else if (/capture_preview:resize/.test(payload.command)) {
      resize(payload)
    }
  }

  function hide() {
    preview.hideEmitter.emit()
  }

  function ready() {
    frameReady = true
    tryShowing()
  }

  function resize(payload) {
    frameHeight = Math.max(
      payload.minHeight,
      payload.fullHeight ? payload.height : payload.height + 50
    )

    writeDom(applyHeight)
    tryShowing()
  }

  function applyHeight() {
    if (frameShown) {
      parent.style.setProperty('height', frameHeight + 'px')
      frame.style.setProperty('height', '100%')
    } else {
      parent.style.removeProperty('height')
      frame.style.setProperty('height', frameHeight + 'px')
    }
  }

  function tryShowing() {
    if (frameShown || !frameReady || frameHeight === null) {
      return
    }

    debouncedShow()
  }

  function lazyShow() {
    writeDom(show)
  }

  function show() {
    if (frameShown) {
      return
    }

    frameShown = true
    applyHeight()

    frame.classList.add('capture-preview__frame--shown')
    preview.loadEmitter.emit()
  }
}

function parsePayload(data) {
  try {
    return JSON.parse(data)
  } catch (_err) {
    return {}
  }
}

function preconfigure(parent) {
  parent.classList.add('capture-preview__parent')

  const className = 'capture-preview__background'
  const foundBackground = parent.getElementsByClassName(className)[0]

  if (foundBackground) {
    return
  }

  const background = buildElement('section', className)

  for (const tileClassName of BACKGROUND_TILES) {
    const tile = buildElement('section', tileClassName)
    background.append(tile)
  }

  parent.append(background)
}

function buildFrameSrc(url) {
  const key = extractKey(url)
  const origin = extendHost(extractOrigin(url))

  if (!key || !origin) {
    throw new Error('The iframe SRC couldn\'t be built!')
  }

  return `${origin}/preview-lead-capture/${key}-x`
}

function extendHost(host) {
  // Conf.preview_host is different for each env:
  //
  //  1. gleam.io -> preview.gleam.io
  //  2. gleamdev.io -> preview.gleamdev.io
  //  3. staging.gleam.io -> staging.gleam.io

  return host?.replace('//gleam', '//preview.gleam')
}

function buildFrame() {
  const frame = buildElement('iframe', 'capture-preview__frame')
  frame.setAttribute('scrolling', 'no')
  return frame
}

function setFrameSrc(frame, frameSrc) {
  frame.src = frameSrc
  return frame
}

function buildElement(tagName, className) {
  const element = document.createElement(tagName)
  element.className = className
  return element
}
